import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HiOutlineOfficeBuilding, 
  HiPlus, 
  HiOutlineSearch, 
  HiOutlineRefresh,
  HiOutlineDotsVertical,
  HiOutlineEmojiSad,
  HiOutlinePencil,
  HiOutlineTrash
} from "react-icons/hi";
import { getOrganizationsAPI, deleteOrganizationAPI } from "../services/organizationService";
import { toast } from "react-toastify";
import CreateOrganizationModal from "../Components/CreateOrganizationModal";
import useAuth from "../useAuth";
import { useNavigate, Navigate } from "react-router-dom";
import DeleteModal from "../Components/DeleteModal";

export default function OrganizationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await getOrganizationsAPI();
      // Assuming the API returns an array or an object with an array
      setOrganizations(Array.isArray(data) ? data : data?.organizations || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.userType !== "admin") {
      navigate("/my/home");
      return;
    }
    fetchOrganizations();
  }, [user, navigate]);

  const confirmDelete = async (item) => {
    setIsDeleting(true);
    try {
      await deleteOrganizationAPI(item.id);
      toast.success("Organization deleted successfully");
      fetchOrganizations();
      setItemToDelete(null);
    } catch (error) {
      toast.error(error || "Failed to delete organization");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user || user.userType !== "admin") {
    return <Navigate to="/my/home" />;
  }

  const filteredOrganizations = organizations.filter(org => 
    org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="relative min-h-screen">
      {/* Header Section */}
      <section className="px-2 pt-2 mb-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-2">
              Organizations
            </h1>
            <p className="text-slate-500 text-[10px] md:text-sm font-bold uppercase tracking-[0.2em]">
              Manage and view all registered entities
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-4 rounded-2xl flex items-center gap-3 shadow-lg shadow-indigo-100 transition-all hover:scale-105 active:scale-95"
          >
            <HiPlus size={20} />
            <span className="text-xs font-black uppercase tracking-widest">New Organization</span>
          </button>
        </div>
      </section>

      {/* Toolbar Section */}
      <section className="mb-8">
        <div className="bg-white border border-slate-200 rounded-[32px] p-3 shadow-xl flex flex-col md:flex-row items-center gap-4">
          <div className="relative flex-1 w-full">
            <div className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400">
              <HiOutlineSearch size={20} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search organizations..."
              className="w-full bg-slate-50 border-none rounded-2xl py-4 pl-14 pr-6 text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-600/20 transition-all"
            />
          </div>
          <button 
            onClick={fetchOrganizations}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-2xl text-slate-500 transition-all"
            title="Refresh"
          >
            <HiOutlineRefresh size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </section>

      {/* Content Section */}
      <section>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white border border-slate-100 rounded-[32px] p-8 h-64 animate-pulse">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-slate-100 rounded-2xl" />
                  <div className="space-y-2">
                    <div className="w-32 h-4 bg-slate-100 rounded" />
                    <div className="w-20 h-3 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="w-full h-3 bg-slate-50 rounded" />
                  <div className="w-5/6 h-3 bg-slate-50 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredOrganizations.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <AnimatePresence>
              {filteredOrganizations.map((org, index) => (
                <motion.div
                  key={org.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-white border border-slate-100 rounded-[24px] p-5 shadow-sm hover:shadow-xl hover:shadow-indigo-100/40 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col"
                >
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-110 ${org.isActive ? 'bg-indigo-600 shadow-indigo-100' : 'bg-slate-400 shadow-slate-100'}`}>
                        <HiOutlineOfficeBuilding className="text-white text-lg" />
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-[8px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-full ${org.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                          {org.isActive ? "Active" : "Inactive"}
                        </span>
                        <div className="flex gap-1 mt-1">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingOrganization(org);
                              setIsModalOpen(true);
                            }}
                            className="p-1 text-slate-500 hover:text-indigo-600 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <HiOutlinePencil size={14} />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemToDelete(org);
                            }}
                            className="p-1 text-slate-500 hover:text-rose-500 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <HiOutlineTrash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-sm font-black text-slate-900 tracking-tight mb-1 group-hover:text-indigo-600 transition-colors line-clamp-1">
                      {org.name}
                    </h3>
                    
                    <p className="text-slate-400 text-[10px] font-bold leading-relaxed line-clamp-2 mb-4 flex-1">
                      {org.description || "No description provided."}
                    </p>

                    <div className="pt-3 border-t border-slate-50 flex items-center justify-between mt-auto">
                      <div className="flex flex-col">
                        <span className="text-[7px] font-bold text-slate-300 uppercase tracking-widest">Created</span>
                        <span className="text-[9px] font-black text-slate-500 tracking-tighter">
                          {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '---'}
                        </span>
                      </div>
                      <button className="text-[9px] font-black text-indigo-600 uppercase tracking-tighter hover:translate-x-1 transition-transform">
                        Details →
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-24 px-4 text-center"
          >
            <div className="w-32 h-32 bg-slate-50 rounded-[48px] flex items-center justify-center mb-8">
              <HiOutlineEmojiSad size={64} className="text-slate-300" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-3">No Organizations Found</h2>
            <p className="text-slate-500 font-bold uppercase tracking-widest text-sm max-w-md mx-auto leading-loose">
              {searchQuery ? "We couldn't find anything matching your search. Try a different keyword." : "Start by creating your first organization to manage entities."}
            </p>
            {!searchQuery && (
              <button 
                onClick={() => setIsModalOpen(true)}
                className="mt-8 bg-slate-900 text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200"
              >
                Create First Organization
              </button>
            )}
          </motion.div>
        )}
      </section>

      <CreateOrganizationModal 
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingOrganization(null);
        }}
        onSuccess={fetchOrganizations}
        organization={editingOrganization}
      />

      <DeleteModal 
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        item={itemToDelete}
        onConfirm={confirmDelete}
        isProcessing={isDeleting}
      />
    </div>
  );
}
